import { ReactRenderer } from '@tiptap/react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import tippy from 'tippy.js';

import { getEditorMenuItems, getTableMenuItems } from './EditorMenuItems';
import './style.css';
import { FormattedMessage } from 'react-intl';

const SlashMenuItemsRenderer = forwardRef((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index) => {
    const item = props.items?.[index];
    if (item && typeof props.command === 'function') {
      props.command(item);
    }
  };

  const upHandler = () => {
    if (props.items && props.items.length > 0) {
      setSelectedIndex((prev) => {
        let newIndex = (prev + props.items.length - 1) % props.items.length;

        if (props?.items?.[newIndex]?.type === 'divider') {
          newIndex = (prev + props.items.length - 2) % props.items.length;
        }

        const el = document.getElementById(`slash-menu-item-${newIndex}`);

        if (el) {
          el.scrollIntoView({ block: 'center' });
        }

        return newIndex;
      });
    }
  };

  const downHandler = () => {
    if (props.items && props.items.length > 0) {
      setSelectedIndex((prev) => {
        let newIndex = (prev + 1) % props.items.length;

        if (props?.items?.[newIndex]?.type === 'divider') {
          newIndex = (prev + 2) % props.items.length;
        }

        const el = document.getElementById(`slash-menu-item-${newIndex}`);

        if (el) {
          el.scrollIntoView({ block: 'center' });
        }

        return newIndex;
      });
    }
  };

  const enterHandler = () => {
    if (props.items && props.items.length > 0) {
      selectItem(selectedIndex);
    }
  };

  useEffect(() => {
    if (props.items && props.items.length > 0) {
      setSelectedIndex(0);
    }
  }, [props.items]);

  useEffect(() => {
    if (props.items && props.items.length > 0 && selectedIndex >= props.items.length) {
      setSelectedIndex(0);
    }
  }, [props.items, selectedIndex]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (!props.items || props.items.length === 0) {
        event.stopPropagation();
        return false;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  const handleMouseEnter = useCallback((index) => () => {
    setSelectedIndex(index);
  }, []);

  return (
    <div
      className="slash-menu-dropdown z-[1000] bg-white border rounded-[4px] overflow-y-auto border-[#ccc] max-h-[200px]"
      style={{
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '4px 0',
      }}
    >
      {(!props.items || props.items.length === 0) && (
        <div className="slash-menu-item no-results">
          <FormattedMessage id="NO_RESULTS" defaultMessage="No results" />
        </div>
      )}
      {props.items.map((item, index) =>
        item.type === 'divider' ? (
          <div key={item.key} className="text-editor-horizontal-divider" />
        ) : (
          <div
            id={`slash-menu-item-${index}`}
            className={`flex items-center cursor-pointer slash-menu-item ${index === selectedIndex ? 'selected' : ''}`}
            key={item.key}
            onClick={() => selectItem(index)}
            onMouseEnter={handleMouseEnter(index)}
          >
            {item.label}
          </div>
        ),
      )}
    </div>
  );
});

export const createSlashMenuSuggestion = (entityId, entityType, linkEditorComponentId, showMentionOptions) => ({
  items: ({ query, editor }) => {
    const items = editor.isActive('table') ?
      getTableMenuItems({
        editor,
        isExtension: true
      }) :
      getEditorMenuItems({
        editor,
        entityId,
        entityType,
        isExtension: true,
        linkEditorComponentId,
        showImageOption: !!(entityType && entityId),
        showMentionOptions,
      });
    return items.filter(item => item?.commandName?.toLowerCase().includes(query.toLowerCase()));
  },
  render: () => {
    let component;
    let popup;
    let isInitialized = false;

    return {
      onStart: (props) => {
        component = new ReactRenderer(SlashMenuItemsRenderer, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          zIndex: 9999,
        });

        isInitialized = true;
      },
      onUpdate(props) {
        if (!component && isInitialized) {
          component = new ReactRenderer(SlashMenuItemsRenderer, {
            props,
            editor: props.editor,
          });
        }

        if (!popup && props.clientRect && component) {
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            zIndex: 9999,
          });
        }

        if (popup) {
          const popupInstance = Array.isArray(popup) ? popup[0] : popup;
          if (popupInstance && popupInstance.show) {
            popupInstance.show();
          }
        }

        if (component) {
          component.updateProps(props);
        }

        if (!props.clientRect || !popup) {
          return;
        }

        const popupInstance = Array.isArray(popup) ? popup[0] : popup;
        if (popupInstance && popupInstance.setProps) {
          popupInstance.setProps({
            getReferenceClientRect: props.clientRect,
          });
        }
      },
      onKeyDown(props) {
        if (['ArrowUp', 'ArrowDown', 'Escape', 'Enter'].includes(props.event.key)) {
          props.event.stopPropagation();
          props.event.preventDefault();
        }

        if (props.event.key === 'Escape') {
          const popupInstance = Array.isArray(popup) ? popup[0] : popup;
          if (popupInstance && popupInstance.hide) {
            popupInstance.hide();
          }
          return true;
        }

        if (component && component.ref && typeof component.ref.onKeyDown === 'function') {
          return component.ref.onKeyDown(props);
        }

        return false;
      },

      onExit() {
        if (popup) {
          const popupInstance = Array.isArray(popup) ? popup[0] : popup;
          if (popupInstance && popupInstance.destroy) {
            popupInstance.destroy();
          }
          popup = null;
        }
        if (component && component.destroy) {
          component.destroy();
          component = null;
        }
        isInitialized = false;
      },
    };
  },
});
